import React from 'react'
import { Subscription } from '../../../interfaces/subscription'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { PricingPlan } from '../../../interfaces/pricingPlan'
import { extractNumberFromString } from '../../../helpers/extractNumberFromString'
import { isGreekBillingCountry } from '../../../helpers/isGreekBillingCountry'

interface Props {
  activeSubscription: Subscription
  pricingPlans: PricingPlan[]
  /** The billing address, or null while it loads and for an account that has
   *  none. Only a Greek one owes VAT on top of the plan price. */
  billingCountry?: string | null
}

const PlanDescription = ({
  activeSubscription,
  pricingPlans = [],
  billingCountry,
}: Props) => {
  const hasPendingPlanChange = activeSubscription?.pending_plan_change
  const pendingPlan = pricingPlans.find(
    (plan) => plan.id === activeSubscription.pending_plan_change,
  )
  if (hasPendingPlanChange && pendingPlan) {
    return (
      <PendingPlanDescription
        pendingPlan={pendingPlan}
        activeSubscription={activeSubscription}
        showVat={isGreekBillingCountry(billingCountry)}
      />
    )
  }

  return (
    <ActivePlanDescription
      activeSubscription={activeSubscription}
      showVat={isGreekBillingCountry(billingCountry)}
    />
  )
}

export default PlanDescription

function ActivePlanDescription({
  activeSubscription,
  showVat,
}: {
  activeSubscription: Subscription
  showVat: boolean
}) {
  const { t } = useTranslation()
  const expirationDate = moment(activeSubscription.current_period_end).format(
    'DD/MM/YYYY',
  )
  const isSubscriptionCancelled = activeSubscription?.cancel_at_period_end
  return (
    <div className='flex flex-col gap-4 text-lg'>
      <p>
        {t('youAreOnThe')}{' '}
        <span className='font-semibold'>
          {activeSubscription.pricing_plan.product.base_name}
        </span>{' '}
        {t('membershipPlan')}.
      </p>
      <p className='text-sm text-[#3D3D3D]'>
        {t('thisSubscriptionIsValidTill')} {expirationDate}.{' '}
        {t(
          isSubscriptionCancelled
            ? 'itWillBeCancelledAtTheEndOfTheBillingPeriod'
            : 'itWillAutomaticallyRenew',
        )}
      </p>
      <p className='text-sm text-[#3D3D3D]'>
        {t('thePriceOfMembership')} €{activeSubscription.pricing_plan.amount}
        {showVat && ` ${t('plusVat')}`}.
      </p>
    </div>
  )
}

function PendingPlanDescription({
  pendingPlan,
  activeSubscription,
  showVat,
}: {
  pendingPlan: PricingPlan
  activeSubscription: Subscription
  showVat: boolean
}) {
  const { t } = useTranslation()
  const planName = pendingPlan.product.base_name
  const isYearly = pendingPlan.product.interval_type === 'yearly'
  // `seats` is null on a product that does not declare one, and
  // `null.includes` would take the whole screen down with it.
  const seats = pendingPlan.product.seats ?? ''
  const isCompany = pendingPlan.product.product_type === 'company'
  const price = pendingPlan.amount
  const unit = isCompany ? t('employees') : t('students')
  const startingDate = moment(activeSubscription.current_period_end).format(
    'DD/MM/YYYY',
  )

  const isUpTo = !seats.includes('>')
  return (
    <div className='flex flex-col gap-4 text-lg'>
      <div className='flex items-center gap-3 flex-wrap-reverse'>
        <p>
          {t('youAreOnThe')} <span className='font-semibold'>{planName}</span>{' '}
          {t('membershipPlan')}.
        </p>
        <p className='bg-inProgressBg text-inProgressText rounded-lg w-[110px] py-1 text-center text-sm font-medium'>
          {t('pending')}
        </p>
      </div>
      <p className='text-sm text-[#3D3D3D]'>
        {t('upToDescription', {
          range: isUpTo ? t('upTo2') : t('moreThan2'),
          seats: extractNumberFromString(seats),
          unit,
          productType: isCompany
            ? t('company')?.toLowerCase()
            : t('yourUniversity'),
        })}
      </p>
      <p className='text-sm text-[#3D3D3D]'>
        {t('pendingPlanDescription', {
          startingDate,
          months: isYearly ? 12 : 1,
          monthsLabel: isYearly ? t('months') : t('month'),
          interpolation: { escapeValue: false },
        })}
      </p>
      <p className='text-sm text-[#3D3D3D]'>
        {t('thePriceOfMembership')} €{price}
        {showVat && `${t('plusVat')}`}.
      </p>
    </div>
  )
}
